import React, { useCallback, useMemo, useState } from 'react'
import SlidingPane from 'react-sliding-pane'
import FieldPicker from './FieldPicker'
import AttributeName from './AttributeName'
import RequiredFlag from './RequiredFlag'
import { buildMapping, isNotAllowed, leftSide, rightSide, sideFields } from '../attributeDirection'
import Shared from '../../Shared/mappingUi.module.css'
import Style from '../productAttribute.module.css'

/**
 * The "add new mappings" workspace: pick one field on the left, one or more on the
 * right, stage the pairs, then hand them all back to the parent. Nothing is saved
 * from here - the parent owns the rows and the save call.
 */
const AddMappingPane = ({
    isOpen,
    onClose,
    direction,
    leftTitle,
    rightTitle,
    erpFields,
    ecommerceFields,
    mappedRightNames,
    onAdd
}) => {
    const [selectedLeft, setSelectedLeft] = useState(null)
    const [selectedRight, setSelectedRight] = useState([])
    const [staged, setStaged] = useState([])
    const [message, setMessage] = useState('')

    const { left: leftFields, right: rightFields } = useMemo(
        () => sideFields(direction, erpFields, ecommerceFields),
        [direction, erpFields, ecommerceFields]
    )

    // A right-hand field already mapped - saved or staged here - is off the list.
    const stagedRightNames = useMemo(
        () => staged.map((mapping) => rightSide(mapping, direction).name),
        [staged, direction]
    )

    const availableRightFields = useMemo(
        () => rightFields.filter((field) => (
            mappedRightNames.indexOf(field.name) === -1 && stagedRightNames.indexOf(field.name) === -1
        )),
        [rightFields, mappedRightNames, stagedRightNames]
    )

    const blockedRightNames = useMemo(
        () => (selectedLeft
            ? availableRightFields.filter((field) => isNotAllowed(selectedLeft, field)).map((field) => field.name)
            : []),
        [selectedLeft, availableRightFields]
    )

    const reset = useCallback(() => {
        setSelectedLeft(null)
        setSelectedRight([])
        setStaged([])
        setMessage('')
    }, [])

    const handleClose = () => {
        reset()
        onClose()
    }

    const pickLeft = (field) => {
        setMessage('')
        setSelectedLeft((current) => (current && current.name === field.name ? null : field))
        setSelectedRight([])
    }

    const pickRight = (field) => {
        setMessage('')
        if (!selectedLeft) {
            setMessage(`Select a ${leftTitle} field first.`)
            return
        }
        if (isNotAllowed(selectedLeft, field)) {
            setMessage(`${leftTitle} field "${selectedLeft.name}" cannot be mapped to ${rightTitle} field "${field.name}".`)
            return
        }
        setSelectedRight((current) => (
            current.some((entry) => entry.name === field.name)
                ? current.filter((entry) => entry.name !== field.name)
                : [...current, field]
        ))
    }

    const stageSelection = () => {
        if (!selectedLeft) {
            setMessage(`Select a ${leftTitle} field.`)
            return
        }
        if (selectedRight.length === 0) {
            setMessage(`Select at least one ${rightTitle} field.`)
            return
        }
        setStaged((current) => [
            ...current,
            ...selectedRight.map((field) => buildMapping(direction, selectedLeft, field))
        ])
        setSelectedLeft(null)
        setSelectedRight([])
        setMessage('')
    }

    const unstage = (index) => {
        setStaged((current) => current.filter((entry, position) => position !== index))
    }

    const confirm = () => {
        if (staged.length === 0) {
            setMessage('Add at least one mapping before confirming.')
            return
        }
        onAdd(staged)
        reset()
        onClose()
    }

    return (
        <SlidingPane
            overlayClassName="some-custom-overlay-class productAttributeMapping"
            isOpen={isOpen}
            title="Add New Product Attribute Mappings"
            subtitle={`${leftTitle} to ${rightTitle}`}
            onRequestClose={handleClose}
        >
            <div className={Style.paneBody}>
                <div className={Style.pickerRow}>
                    <FieldPicker
                        title={leftTitle}
                        placeholder={`Search ${leftTitle} fields`}
                        fields={leftFields}
                        selectedNames={selectedLeft ? [selectedLeft.name] : []}
                        disabledNames={[]}
                        onSelect={pickLeft}
                    />
                    <div className={Style.pickerBridge}>
                        <span className={Style.pickerBridgeArrow} aria-hidden="true">&rarr;</span>
                        <button
                            type="button"
                            className={Shared.submitButton}
                            disabled={!selectedLeft || selectedRight.length === 0}
                            onClick={stageSelection}
                        >
                            Add pair
                        </button>
                    </div>
                    <FieldPicker
                        title={rightTitle}
                        placeholder={`Search ${rightTitle} fields`}
                        fields={availableRightFields}
                        selectedNames={selectedRight.map((field) => field.name)}
                        disabledNames={blockedRightNames}
                        onSelect={pickRight}
                    />
                </div>

                {message ? <p className={Style.paneMessage} role="alert">{message}</p> : null}

                <div className={Style.paneFooter}>
                    <span className={Shared.countChip}>{staged.length} staged</span>
                    <button
                        type="button"
                        className={Shared.submitButton}
                        disabled={staged.length === 0}
                        onClick={confirm}
                    >
                        Add to mappings
                    </button>
                </div>

                {staged.length === 0 ? (
                    <p className={Shared.hint}>
                        Pick a {leftTitle} field, then one or more {rightTitle} fields, and add the pair.
                        Incompatible data types are greyed out.
                    </p>
                ) : (
                    <table className={Style.stagedTable}>
                        <thead>
                            <tr>
                                <th scope="col">{leftTitle} Field</th>
                                <th scope="col" className={Style.centerCell}>Required</th>
                                <th scope="col">{rightTitle} Field</th>
                                <th scope="col" className={Style.centerCell}>Required</th>
                                <th scope="col" className={Style.centerCell}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staged.map((mapping, index) => {
                                const left = leftSide(mapping, direction)
                                const right = rightSide(mapping, direction)
                                return (
                                    <tr key={`${left.name}-${right.name}`}>
                                        <td><AttributeName name={left.name} dataType={left.dataType} /></td>
                                        <td className={Style.centerCell}><RequiredFlag isRequired={left.isRequired} /></td>
                                        <td><AttributeName name={right.name} dataType={right.dataType} /></td>
                                        <td className={Style.centerCell}><RequiredFlag isRequired={right.isRequired} /></td>
                                        <td className={Style.centerCell}>
                                            <button
                                                type="button"
                                                className={Shared.deleteButton}
                                                title="Remove staged mapping"
                                                aria-label={`Remove staged mapping for ${left.name}`}
                                                onClick={() => unstage(index)}
                                            >
                                                <i className="uil-trash-alt" aria-hidden="true" />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </SlidingPane>
    )
}

export default AddMappingPane
